/**
 * @file interface.ts
 * @description 剧本创作/详情页接口类型
 * @author ares
 * @date 2026-04-13
 */
import type {ScriptId, ScriptType} from '@/store/script/interface';
import type {ProjectId} from '@/types';

/* 解析状态 */
export const PARSE_STATUS = {
    // 解析中
    PARSING: 0,
    // 解析成功
    SUCCESS: 1,
    // 解析失败
    FAILED: 2,
};
export type ParseStatus = (typeof PARSE_STATUS)[keyof typeof PARSE_STATUS];

// 解析状态信息
export interface ParseInfo {
    /* 状态 */
    status: ParseStatus;

    /* 提示信息 */
    tips?: string;

    /* 剩余时间(分钟) */
    remainTime?: number;
}

/* 剧本状态 */
export enum ScriptStatus {
    /* 失败态 */
    FAILED = -1,

    /* 默认态，即解析中 */
    DEFAULT = 0,

    /* 开发态 */
    DEVELOPING = 1,

    /* 提交态（未确认） */
    SUBMITTED = 2,

    /* 已确认 */
    CONFIRMED = 3,

    /* 已驳回 */
    REJECTED = 4,
}

export const EDIT_STATUS = {
    VIEW: 0,
    EDIT: 1,
} as const;

export type EditStatus = (typeof EDIT_STATUS)[keyof typeof EDIT_STATUS];

/* 文件类型 */
export const FILE_TYPE = {
    // 剧本
    SCRIPT: 1,
    // 小说
    NOVEL: 2,
};

export type FileType = (typeof FILE_TYPE)[keyof typeof FILE_TYPE];

/* 剧本详情 */
export interface ScriptDetail {
    // 解析相关内容
    parseInfo: ParseInfo;
    // 剧本标题
    title: string;
    // 编辑是否锁定状态
    editLocked: boolean;
    // 编辑状态(当前用户是否处于编辑态)
    editStatus: EditStatus;
    // 编辑者
    editor: string;
    // 操作者（提交者）
    operator: string;
    // 提交时间
    submitTime: string;
    // 剧本状态
    scriptStatus: ScriptStatus;
    // 剧本状态提示信息
    scriptMsg: string;
    // 更新时间
    updateTime: string;
    // 剧本内容
    detail: {
        // 梗概加载状态
        scriptSummaryStatus: ParseStatus;
        // 梗概内容（markdown字符）
        scriptSummary: string;
        // 梗概富格式内容
        scriptSummaryFormat: string;
        // 剧集加载状态
        scriptEpisodeStatus: ParseStatus;
        // 剧集内容（markdown字符）
        scriptEpisode: string;
        // 剧集富格式内容
        scriptEpisodeFormat: string;
        // 梗概评论（json字符串）
        summaryComment: string;
        // 剧集评论（json字符串）
        episodeComment: string;
    };
}

/* 剧本详情入参 */
export interface ScriptDetailParams {
    scriptId?: ScriptId;
    projectId?: ProjectId;
    source?: string; // 可选参数，标识请求来源（如编辑页、详情页等）
}

/* 剧本保存 */
export interface SaveScriptParams {
    scriptId: ScriptId;
    text: string;
    // 富格式内容
    format?: string;
    type: ScriptType;
    comment?: string;
    source?: FileType;
}

/* 导演剧本操作 */
export enum OperateType {
    CONFIRM = 1,
    REJECT = 2,
}

export interface ReviewScriptParams {
    scriptId: ScriptId;
    type: OperateType;
}

/* 编辑态切换 */
export interface EditScriptParams {
    scriptId: ScriptId;
    edit: boolean;
}

export interface StartParseScriptParams {
    // 项目id
    projectId?: ProjectId;
    // 剧本id （重新解析传入剧本id）
    scriptId?: ScriptId;
    // 文件url（如果是上传文件解析）
    fileUrl?: string;
    // 文件大小
    fileSize?: number;
    // 文件名
    fileName?: string;
    // 文件类型
    type: FileType;
}

export interface ParseScriptResult {
    scriptId: string;
}

/* ---- 剧本列表相关类型（原 scripts/interface.ts） ---- */

/** 剧本列表请求参数 */
export interface ScriptListParams {
    /** 搜索关键词，模糊匹配剧本名称 */
    keyword?: string;
    /** 页码，从 1 开始 */
    page: number;
    /** 每页条数，默认 20 */
    pageSize: number;
}

/** 剧本列表项 */
export interface ScriptItem {
    /** 剧本唯一 ID */
    scriptId: string;
    /** 关联项目 ID，创作模式剧本可能为空 */
    projectId?: ProjectId | null;
    /** 剧本名称 */
    scriptName: string;
    /** 剧本解析状态：0 解析中，1 解析成功，2 解析失败 */
    status: ParseStatus;
    /** 剧本大纲内容，为空时前端展示兜底图 */
    outline: string;
    /** 当前登录用户是否是该剧本的创建人 */
    isCreator: boolean;
    /** 任务提交时间 YYYY-MM-DD HH:mm:ss */
    createdAt: string;
    /** 任务更新时间 YYYY-MM-DD HH:mm:ss */
    updatedAt: string;
}

/** 剧本列表响应数据 */
export interface ScriptListData {
    /** 符合条件的剧本总数 */
    total: number;
    /** 是否还有更多数据 */
    hasMore: boolean;
    /** 剧本列表 */
    list: ScriptItem[];
}

/** 剧本重命名请求参数 */
export interface ScriptRenameParams {
    /** 剧本 ID */
    scriptId: string;
    /** 新的剧本名称，最长 30 字 */
    scriptName: string;
}

/** 剧本删除请求参数 */
export interface ScriptDeleteParams {
    /** 剧本 ID */
    scriptId: string;
}

/** 云端剧本项 */
export interface CloudScriptItem {
    scriptId: string;
    scriptName: string;
    scriptDetail: string;
    createTime: number;
}

/** 云端剧本导入请求参数 */
export interface ImportCloudScriptParams {
    projectId: string;
    scriptId: string;
}

/** 云端剧本导入返回值 */
export interface ImportCloudScriptResult {
    scriptId: string;
}

/** 剧本配置返回值 */
export interface ScriptConfigResult {
    // 小说上传字数限制
    novelWordLimit: string;
    // 剧本上传字数限制
    scriptWordLimit: string;
    // 积分文案
    pointsText: string;
    // 积分规则图片
    pointsRuleImage: string;
    // agent ID
    agentId: string;
}
